import { Alert, Button, Col, Form, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import DynamicForm from "../controls/DynamicForm";
import InModal from "../controls/InModal";
import { FiscalPeriod } from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import {
  addCompanyFiscalPeriod,
  deleteCompanyFiscalPeriod,
  getCompanyFiscalPeriod,
  getCompanyFiscalPeriods,
  updateCompanyFiscalPeriod,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { periodTypeOptions } from "./FiscalPeriodsView.default";
import { FiscalPeriodForm } from "./FiscalPeriodView.default";

export interface FiscalPeriodViewProps {
  readonly uid?: string | null;
  readonly companyUid: string;
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

function FiscalPeriodView(props: FiscalPeriodViewProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<FiscalPeriodForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Something went wrong, try again");
  const [item, setItem] = useState<FiscalPeriod | null>(null);

  // Parent options cascade off PeriodType (Quarter -> Semester, Semester -> Year; a Year has no parent). The backend
  // enforces this too — the picker just guides toward valid choices.
  const parentRequest = async () => {
    const pt = (form.getFieldValue("periodType") as any)?.value as string | undefined;
    if (!pt || pt === "Year") return [];
    const parentType = pt === "Quarter" ? "Semester" : "Year";
    const { data } = await getCompanyFiscalPeriods({
      searchQuery: {
        conditions: [
          { field: "companyUid", operation: QueryOperation.Equal, values: [props.companyUid] },
          { field: "periodType", operation: QueryOperation.Equal, values: [parentType] },
        ],
      },
      pageNumber: 0,
      pageSize: 200,
    });
    return data
      .filter((p) => p.uid !== props.uid)
      .map((p) => ({ display: p.name, value: p.uid }));
  };

  useEffect(() => {
    if (props.open) loadData();
    else reset();
  }, [props.open, props.uid]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((v) => setValid(!!Object.values(v).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function loadData(): void {
    if ((props.mode !== "update" && props.mode !== "delete") || !props.uid) {
      reset();
      return;
    }
    setLoading(true);
    setError(false);
    getCompanyFiscalPeriod(props.uid)
      .then((p) => {
        setLoading(false);
        setItem(p);
        form.setFieldsValue({
          name: p.name,
          periodType: { display: p.periodType, value: p.periodType },
          year: p.year,
          startDate: p.startDate ? dayjs(p.startDate) : undefined,
          endDate: p.endDate ? dayjs(p.endDate) : undefined,
          parent: p.parentFiscalPeriodUid
            ? { display: p.parentName, value: p.parentFiscalPeriodUid }
            : undefined,
        });
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    setItem(null);
    if (props.open) form.resetFields();
  }

  function done(): void {
    setLoading(false);
    props.onFinish?.();
  }

  function fail(e: any): void {
    setErrorMessage(getProblemMessage(e));
    setLoading(false);
    setError(true);
  }

  function onSubmit(): void {
    setLoading(true);
    setError(false);

    if (props.mode === "delete") {
      deleteCompanyFiscalPeriod(props.uid!).then(done).catch(fail);
      return;
    }

    const { name, periodType, year, startDate, endDate, parent } = values;
    const payload = {
      name,
      periodType: (periodType as any)?.value,
      year: +year,
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
      parentFiscalPeriodUid: (parent as any)?.value,
      companyUid: props.companyUid,
    };

    if (props.mode === "add") {
      addCompanyFiscalPeriod(payload as any).then(done).catch(fail);
    } else {
      updateCompanyFiscalPeriod({
        uid: item?.uid,
        rowVersion: item?.rowVersion,
        ...payload,
      } as any)
        .then(done)
        .catch(fail);
    }
  }

  const title =
    (props.mode === "add" ? "Add" : props.mode === "update" ? "Edit" : "Delete") +
    " Fiscal Period";

  return (
    <InModal
      open={props.open}
      title={title}
      size="small"
      onCancel={() => props.onClose?.()}
      actions={
        <Space className="sticky bottom-0 flex flex-row items-center justify-end">
          <Button disabled={loading} onClick={() => props.onClose?.()}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger={props.mode === "delete"}
            loading={loading}
            disabled={props.mode !== "delete" && !valid}
            onClick={onSubmit}
          >
            {props.mode === "add" ? "Add" : props.mode === "update" ? "Save" : "Delete"}
          </Button>
        </Space>
      }
    >
      <Col className="flex flex-col">
        {props.mode !== "delete" && (
          <DynamicForm
            className="w-auto"
            form={form}
            schema={{
              groups: [
                {
                  controls: [
                    {
                      type: "textbox",
                      name: "name",
                      label: "Name",
                      rules: [{ required: true, message: "Name is required" }],
                    },
                    {
                      type: "combobox",
                      name: "periodType",
                      label: "Period Type",
                      request: periodTypeOptions,
                      rules: [{ required: true, message: "Period type is required" }],
                    },
                    {
                      type: "numberbox",
                      name: "year",
                      label: "Year",
                      rules: [{ required: true, message: "Year is required" }],
                    },
                    {
                      type: "combobox",
                      name: "parent",
                      label: "Parent Period",
                      // Resets when Period Type changes; required for Semester/Quarter (a Year has no parent).
                      dependsOn: ["periodType"],
                      request: parentRequest,
                    },
                    {
                      type: "datepicker",
                      name: "startDate",
                      label: "Start Date",
                      rules: [{ required: true, message: "Start date is required" }],
                    },
                    {
                      type: "datepicker",
                      name: "endDate",
                      label: "End Date",
                      rules: [{ required: true, message: "End date is required" }],
                    },
                  ],
                },
              ],
            }}
          />
        )}
        {props.mode === "delete" && (
          <Typography.Paragraph>
            <Typography.Text>
              Continue deleting the{" "}
              <Typography.Text className="font-bold">{item?.name}</Typography.Text> fiscal
              period?
            </Typography.Text>
          </Typography.Paragraph>
        )}
        {error && <Alert className="mb-2" message={errorMessage} type="error" />}
      </Col>
    </InModal>
  );
}

export default FiscalPeriodView;
